import { useCallback } from "react";

import { DRIVE_CONSTANTS } from "@/constants/Drive.constant";
import { ONE_MB, TWO } from "@/constants/constants";
import { BatchFileItem, BatchFolderItem } from "@/interfaces/project.interface";
import { customToast } from "@/utils/custom-toast";

export const useFolderUpload = (): {
  processFileSystemEntries: (
    entries: FileSystemEntry[],
    parentFolderId?: string | null,
  ) => Promise<{ files: BatchFileItem[]; folders: BatchFolderItem[] }>;
  processWebkitDirectoryFiles: (fileList: File[]) => {
    files: BatchFileItem[];
    folders: BatchFolderItem[];
  };
} => {
  const processFileSystemEntries = useCallback(
    async (
      entries: FileSystemEntry[],
    ): Promise<{ files: BatchFileItem[]; folders: BatchFolderItem[] }> => {
      const parentPath: string[] = [];

      const newFiles: BatchFileItem[] = [];
      const newFolders: BatchFolderItem[] = [];
      const processedFolders = new Set<string>();

      const extractFilesAndFolders = async (
        entry: FileSystemEntry,
        currentPath: string[],
      ): Promise<void> => {
        if (entry.isDirectory) {
          const dirEntry = entry as FileSystemDirectoryEntry;
          const folderPath = [...currentPath];
          const folderName = dirEntry.name;

          const folderKey = [...folderPath, folderName].join("/");

          if (!processedFolders.has(folderKey)) {
            processedFolders.add(folderKey);

            newFolders.push({
              name: folderName,
              path: folderPath,
            });
          }

          await new Promise<void>((resolve) => {
            const reader = dirEntry.createReader();

            const readEntries = (): void => {
              reader.readEntries(async (subEntries) => {
                if (subEntries.length > 0) {
                  for (const subEntry of subEntries) {
                    await extractFilesAndFolders(subEntry, [
                      ...folderPath,
                      folderName,
                    ]);
                  }
                  readEntries();
                } else {
                  resolve();
                }
              });
            };

            readEntries();
          });
        } else {
          const fileEntry = entry as FileSystemFileEntry;

          await new Promise<void>((resolve) => {
            fileEntry.file((file) => {
              if (file.size > DRIVE_CONSTANTS.MAX_FILE_SIZE) {
                customToast.error(
                  `File ${file.name} exceeds size limit (${(
                    DRIVE_CONSTANTS.MAX_FILE_SIZE / ONE_MB
                  ).toFixed(TWO)}MB)`,
                );
                resolve();

                return;
              }

              newFiles.push({
                file,
                path: currentPath,
                status: "pending",
                progress: 0,
              });

              resolve();
            });
          });
        }
      };

      for (const entry of entries) {
        await extractFilesAndFolders(entry, parentPath);
      }

      return { files: newFiles, folders: newFolders };
    },
    [],
  );

  const processWebkitDirectoryFiles = useCallback(
    (
      fileList: File[],
    ): { files: BatchFileItem[]; folders: BatchFolderItem[] } => {
      const folderPaths = new Map<string, string[]>();
      const newFolders: BatchFolderItem[] = [];
      const newFiles: BatchFileItem[] = [];
      const basePath: string[] = [];

      fileList.forEach((file) => {
        if (!file.webkitRelativePath) {
          return;
        }

        const pathParts = file.webkitRelativePath.split("/");
        const [rootFolderName] = pathParts;

        if (!rootFolderName) {
          return;
        }

        let currentPath = [...basePath];

        for (let i = 0; i < pathParts.length - 1; i++) {
          const folderName = pathParts[i];
          const pathKey = [...currentPath, folderName].join("/");

          if (!folderPaths.has(pathKey)) {
            folderPaths.set(pathKey, currentPath);
            newFolders.push({
              name: folderName,
              path: [...currentPath],
            });
          }

          currentPath = [...currentPath, folderName];
        }

        if (file.size > DRIVE_CONSTANTS.MAX_FILE_SIZE) {
          customToast.error(
            `File ${file.name} exceeds size limit (${(
              DRIVE_CONSTANTS.MAX_FILE_SIZE / ONE_MB
            ).toFixed(TWO)} MB)`,
          );

          return;
        }

        newFiles.push({
          file,
          path: currentPath,
          status: "pending",
          progress: 0,
        });
      });

      return { files: newFiles, folders: newFolders };
    },
    [],
  );

  return {
    processFileSystemEntries,
    processWebkitDirectoryFiles,
  };
};
